import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { toast } from "sonner";
import { SignatureCanvas } from "./SignatureCanvas";
import { Camera, MapPin, CheckCircle2, X, Check, AlertTriangle } from "lucide-react";

export const CODIGOS_DIV = [
  { value: "OF", label: "OF — Faltou" },
  { value: "OA", label: "OA — Avariado" },
  { value: "OE", label: "OE — Item errado" },
  { value: "OS", label: "OS — Ficou na base" },
];

const FOTO_OBRIGATORIA = ["OA", "OE"]; // OF/OS: foto opcional (não há o que fotografar)

export const comprimirImagem = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const max = 1280;
    const escala = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * escala);
    c.height = Math.round(img.height * escala);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    resolve(c.toDataURL("image/jpeg", 0.6));
    URL.revokeObjectURL(img.src);
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

const FotoInput = ({ valor, onChange, testid, pendente, opcional }) => (
  <label data-testid={`${testid}-wrap`}
    className={`flex items-center gap-2 text-xs font-medium rounded-lg border-2 px-3 py-2.5 cursor-pointer ${valor ? "bg-emerald-50 border-emerald-300 text-emerald-700" : pendente ? "bg-white border-red-400 text-red-600" : "bg-white border-slate-300 text-slate-600"}`}>
    <Camera className="h-4 w-4" />
    {valor ? "Foto anexada ✓ (tocar para trocar)" : opcional ? "Tirar foto (opcional)" : "Tirar foto / escolher da galeria *"}
    <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={testid}
      onChange={async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try { onChange(await comprimirImagem(f)); }
        catch { toast.error("Não foi possível ler esta imagem. Tente outra foto (JPG/PNG)."); }
      }} />
  </label>
);

const chaveRascunho = (romaneio) => `conf-rascunho-${romaneio.id}`;

export const limparRascunhoConferencia = (romaneioId) => {
  try { localStorage.removeItem(`conf-rascunho-${romaneioId}`); } catch { /* sem storage */ }
};

const nomeValido = (nome) => nome.trim().split(/\s+/).filter(p => p.length >= 2).length >= 2;
const fmtQtd = (q) => Number(q ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

// Form compartilhado: conferencia digital (publica, item a item com ✓/✗) e lancamento do papel (logado)
export const ConferenciaForm = ({ romaneio, modo = "digital", onSubmit, enviando }) => {
  // NENHUM item vem marcado; quantidades NAO vem preenchidas
  const [linhas, setLinhas] = useState(() => {
    let rascunho = null;
    try { rascunho = JSON.parse(localStorage.getItem(chaveRascunho(romaneio)) || "null"); } catch { rascunho = null; }
    return romaneio.linhas.map(l => {
      const salvo = rascunho?.linhas?.find(x => x.linha_id === l.linha_id);
      return { ...l, marcado: salvo?.marcado || null, qtd_boa: salvo?.qtd_boa ?? "", qtd_problema: salvo?.qtd_problema ?? "",
               codigo_div: salvo?.codigo_div || "", descricao_div: salvo?.descricao_div || "",
               foto_base64: salvo?.foto_base64 || null };
    });
  });
  const [recebedor, setRecebedor] = useState(() => {
    try { return JSON.parse(localStorage.getItem(chaveRascunho(romaneio)) || "null")?.recebedor || ""; } catch { return ""; }
  });
  const [funcao, setFuncao] = useState("");
  const [vinculo, setVinculo] = useState("");
  const [assinatura, setAssinatura] = useState(null);
  const [foto2via, setFoto2via] = useState(null);
  const [geo, setGeo] = useState(null);

  // Se a conexao cair, as marcacoes ficam guardadas no aparelho
  useEffect(() => {
    try {
      localStorage.setItem(chaveRascunho(romaneio), JSON.stringify({
        recebedor,
        linhas: linhas.map(l => ({ linha_id: l.linha_id, marcado: l.marcado, qtd_boa: l.qtd_boa, qtd_problema: l.qtd_problema,
                                   codigo_div: l.codigo_div, descricao_div: l.descricao_div, foto_base64: l.foto_base64 })),
      }));
    } catch { /* storage cheio: segue sem rascunho */ }
  }, [linhas, recebedor, romaneio]);

  const setLinha = (i, patch) => setLinhas(ls => ls.map((x, j) => j === i ? { ...x, ...patch } : x));
  const marcarOk = (i) => setLinha(i, { marcado: "ok", qtd_boa: "", qtd_problema: "", codigo_div: "", descricao_div: "", foto_base64: null });
  const marcarDiv = (i) => setLinha(i, { marcado: "div", qtd_boa: "", qtd_problema: "" });

  // Soma automatica: digitou um lado, o outro completa ate a quantidade enviada
  const setQtds = (i, campo, valor) => {
    const env = Number(linhas[i].quantidade);
    let v = valor;
    if (v !== "" && Number(v) > env) v = String(env);
    if (v !== "" && Number(v) < 0) v = "0";
    const patch = campo === "boa" ? { qtd_boa: v } : { qtd_problema: v };
    if (v !== "" && !Number.isNaN(Number(v))) {
      const outro = String(Math.round((env - Number(v)) * 100) / 100);
      if (campo === "boa") patch.qtd_problema = outro; else patch.qtd_boa = outro;
    }
    setLinha(i, patch);
  };

  const qtdsValidas = (l) => {
    const env = Number(l.quantidade), b = Number(l.qtd_boa), p = Number(l.qtd_problema);
    return l.qtd_boa !== "" && l.qtd_problema !== "" && !Number.isNaN(b) && !Number.isNaN(p)
      && b >= 0 && p > 0 && Math.abs(b + p - env) < 0.009;
  };
  const fotoObrig = (l) => FOTO_OBRIGATORIA.includes(l.codigo_div);
  const linhaCompleta = (l) => l.marcado === "ok" ||
    (l.marcado === "div" && qtdsValidas(l) && !!l.codigo_div && (l.descricao_div || "").trim().length >= 3
      && (!fotoObrig(l) || !!l.foto_base64));
  const completos = linhas.filter(linhaCompleta).length;

  // Lista ordenada de pendencias, com alvo para rolagem/destaque e texto do aviso
  const pendencias = [];
  linhas.forEach((l, i) => {
    if (!l.marcado) pendencias.push({ sel: `[data-testid="conf-linha-${i}"]`, toast: `conferir "${l.nome}"` });
    else if (l.marcado === "div") {
      if (!qtdsValidas(l)) pendencias.push({ sel: `[data-testid="conf-qtd-boa-${i}"]`, toast: `quantidades (bom estado + com problema = ${fmtQtd(l.quantidade)}) em ${l.nome}` });
      if (!l.codigo_div) pendencias.push({ sel: `[data-testid="conf-codigo-${i}"]`, toast: `motivo em ${l.nome}` });
      if ((l.descricao_div || "").trim().length < 3) pendencias.push({ sel: `[data-testid="conf-descricao-${i}"]`, toast: `descrição em ${l.nome}` });
      if (fotoObrig(l) && !l.foto_base64) pendencias.push({ sel: `[data-testid="conf-foto-${i}-wrap"]`, toast: `foto em ${l.nome}` });
    }
  });
  if (!nomeValido(recebedor)) pendencias.push({ sel: '[data-testid="input-recebedor"]', toast: "nome completo de quem recebe (nome e sobrenome)" });
  if (modo === "digital" && !assinatura) pendencias.push({ sel: '[data-testid="assinatura-wrap"]', toast: "assinatura desenhada na tela" });
  if (modo === "papel" && !foto2via) pendencias.push({ sel: '[data-testid="foto-2via-wrap"]', toast: "foto da 2ª via assinada" });

  const irParaPendencia = () => {
    const p = pendencias[0];
    const el = document.querySelector(p.sel);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      try {
        el.animate([{ boxShadow: "0 0 0 5px rgba(220,38,38,0.65)" }, { boxShadow: "0 0 0 0 rgba(220,38,38,0)" }],
          { duration: 1400, easing: "ease-out" });
      } catch { /* animacao opcional */ }
    }
    if (navigator.vibrate) navigator.vibrate(120);
    toast.error(`Falta: ${p.toast}`);
  };

  const escolherVinculo = (v) => {
    setVinculo(v);
    const f = (romaneio.vinculos || []).find(x => x.id === v);
    if (f?.nome) setRecebedor(f.nome);
  };

  const pedirGeo = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setGeo({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setGeo(null), { timeout: 8000 });
  };

  const enviar = () => {
    onSubmit({
      linhas: linhas.map(l => {
        const divL = l.marcado === "div";
        const boa = divL ? (Number(l.qtd_boa) || 0) : Number(l.quantidade);
        return {
          linha_id: l.linha_id,
          qtd_recebida: boa, qtd_boa: boa,
          qtd_problema: divL ? (Number(l.qtd_problema) || 0) : 0,
          problema: divL,
          codigo_div: divL ? l.codigo_div : null,
          descricao_div: divL ? l.descricao_div : null,
          foto_base64: divL ? l.foto_base64 : null,
        };
      }),
      recebedor_nome: recebedor.trim(), recebedor_funcao: funcao.trim() || null,
      vinculo_funcionario_id: vinculo || null, assinatura_base64: assinatura,
      geo, foto_2via_base64: foto2via,
    });
  };

  const pronto = pendencias.length === 0;
  const nomePendente = recebedor.trim() !== "" && !nomeValido(recebedor);

  return (
    <div className="space-y-3" data-testid="conferencia-form">
      {/* Contador fixo no topo — conta apenas itens COMPLETOS */}
      <div className={`sticky top-0 z-20 rounded-lg px-4 py-2.5 text-sm font-bold text-center shadow-md ${completos === linhas.length ? "bg-emerald-600 text-white" : "bg-luvcorp-navy text-white"}`} data-testid="contador-conferidos">
        {completos} de {linhas.length} itens completos
      </div>

      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800">
        <b>Confira item por item, contando na hora.</b> Toque <b>✓ Recebido completo</b> ou <b>✗ Não veio / com problema</b> em cada item. Nada vem marcado.
      </div>

      {linhas.map((l, i) => {
        const div = l.marcado === "div";
        const ok = l.marcado === "ok";
        const pendente = div && !linhaCompleta(l);
        return (
          <div key={l.linha_id}
            className={`rounded-xl border-2 p-3 space-y-2 ${div ? "border-red-400 bg-red-50" : ok ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"}`}
            data-testid={`conf-linha-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium text-sm text-luvcorp-navy">{l.nome} <span className="text-slate-400 text-xs">{l.codigo}</span></p>
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className={l.tipo === "material" ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-amber-50 text-amber-700 border-amber-300"}>
                  {l.tipo === "material" ? "MATERIAL" : "PATRIMÔNIO"}
                </Badge>
                {ok && <Badge className="bg-emerald-600 text-white">OK</Badge>}
                {div && !pendente && <Badge className="bg-red-600 text-white">DIV</Badge>}
                {pendente && <Badge className="bg-red-600 text-white animate-pulse" data-testid={`selo-pendente-${i}`}><AlertTriangle className="h-3 w-3 mr-1" />PENDENTE</Badge>}
              </div>
            </div>
            <p className="text-xs text-slate-600">Enviado: <b>{fmtQtd(l.quantidade)} {l.unidade}</b></p>

            {/* Dois botoes grandes, faceis de tocar */}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => marcarOk(i)} data-testid={`conf-ok-${i}`}
                className={`flex items-center justify-center gap-1.5 rounded-xl border-2 py-3 text-sm font-bold active:scale-95 transition ${ok ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-emerald-400 text-emerald-700"}`}>
                <Check className="h-5 w-5" /> Recebido completo
              </button>
              <button onClick={() => marcarDiv(i)} data-testid={`conf-div-${i}`}
                className={`flex items-center justify-center gap-1.5 rounded-xl border-2 py-3 text-sm font-bold active:scale-95 transition ${div ? "bg-red-600 border-red-600 text-white" : "bg-white border-red-400 text-red-700"}`}>
                <X className="h-5 w-5" /> Não veio / com problema
              </button>
            </div>

            {div && (
              <div className="space-y-2 border-t border-red-200 pt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 mb-1">Chegaram em bom estado *</p>
                    <Input type="number" inputMode="decimal" min="0" max={l.quantidade} step="any" value={l.qtd_boa}
                      data-testid={`conf-qtd-boa-${i}`} placeholder="0"
                      onChange={(e) => setQtds(i, "boa", e.target.value)}
                      className={`h-11 text-base ${!qtdsValidas(l) ? "border-2 border-red-400" : ""}`} />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 mb-1">Com problema *</p>
                    <Input type="number" inputMode="decimal" min="0" max={l.quantidade} step="any" value={l.qtd_problema}
                      data-testid={`conf-qtd-prob-${i}`} placeholder="0"
                      onChange={(e) => setQtds(i, "prob", e.target.value)}
                      className={`h-11 text-base ${!qtdsValidas(l) ? "border-2 border-red-400" : ""}`} />
                  </div>
                </div>
                <p className="text-[11px] text-slate-500">A soma deve dar {fmtQtd(l.quantidade)} {l.unidade} (enviado). Ex.: 2 bons + 3 com problema.</p>
                <div className={!l.codigo_div ? "rounded-lg ring-2 ring-red-400" : ""}>
                  <NativeSelect value={l.codigo_div} onValueChange={(v) => {
                    const patch = { codigo_div: v };
                    if (v === "OS" && l.qtd_problema === "") { patch.qtd_problema = String(l.quantidade); patch.qtd_boa = "0"; }
                    setLinha(i, patch);
                  }}
                    placeholder="Motivo (do que tem problema) *" options={CODIGOS_DIV} data-testid={`conf-codigo-${i}`} />
                </div>
                {l.codigo_div === "OS" && (
                  <p className="text-[11px] text-amber-700">OS (ficou na base): a quantidade "com problema" volta ao estoque automaticamente.</p>
                )}
                {l.codigo_div === "OA" && (
                  <p className="text-[11px] text-amber-700">OA (avariado): a quantidade "com problema" entra no retorno à base com estado D (danificado).</p>
                )}
                <Textarea value={l.descricao_div} onChange={(e) => setLinha(i, { descricao_div: e.target.value })}
                  placeholder="O que aconteceu? (obrigatório)" rows={2} data-testid={`conf-descricao-${i}`}
                  className={(l.descricao_div || "").trim().length < 3 ? "border-2 border-red-400" : ""} />
                <FotoInput valor={l.foto_base64} onChange={(v) => setLinha(i, { foto_base64: v })}
                  testid={`conf-foto-${i}`} pendente={fotoObrig(l) && !l.foto_base64} opcional={!fotoObrig(l)} />
              </div>
            )}
          </div>
        );
      })}

      <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-3">
        <p className="text-xs font-semibold text-slate-500 uppercase">Recebido por (obra)</p>
        <div>
          <p className="text-[11px] font-semibold text-slate-600 mb-1">Funcionário (opcional)</p>
          <NativeSelect value={vinculo} onValueChange={escolherVinculo} placeholder="Escolher funcionário cadastrado"
            options={(romaneio.vinculos || []).map(f => ({ value: f.id, label: f.nome }))} data-testid="select-vinculo" />
        </div>
        <div>
          <p className="text-[11px] font-semibold text-slate-600 mb-1">Nome completo *</p>
          <Input value={recebedor} onChange={(e) => setRecebedor(e.target.value)} placeholder="Ex.: Jorge da Silva"
            data-testid="input-recebedor" className={`h-11 ${nomePendente ? "border-2 border-red-400" : ""}`} />
          <p className={`text-[11px] mt-0.5 ${nomePendente ? "text-red-600 font-semibold" : "text-slate-400"}`}>Nome e sobrenome</p>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-slate-600 mb-1">Função</p>
          <Input value={funcao} onChange={(e) => setFuncao(e.target.value)} placeholder="Ex.: encarregado, zelador" data-testid="input-funcao" className="h-11" />
        </div>
        {modo === "digital" && (
          <div data-testid="assinatura-wrap">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Assinatura (desenhe na tela) *</p>
            <SignatureCanvas onChange={setAssinatura} />
          </div>
        )}
        {modo === "papel" && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Foto da 2ª via assinada *</p>
            <FotoInput valor={foto2via} onChange={setFoto2via} testid="foto-2via" pendente={!foto2via} />
          </div>
        )}
        <button onClick={pedirGeo} data-testid="btn-geolocalizacao"
          className={`flex items-center gap-1.5 text-xs rounded-lg border px-3 py-2 ${geo ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-300 text-slate-500"}`}>
          <MapPin className="h-3.5 w-3.5" /> {geo ? `Localização registrada (${geo.lat.toFixed(4)}, ${geo.lng.toFixed(4)})` : "Registrar localização do aparelho (opcional)"}
        </button>
      </div>

      {/* Botao: cinza enquanto faltar algo; verde quando completo; bloqueado durante envio */}
      <Button onClick={pronto ? enviar : irParaPendencia} disabled={enviando}
        className={`w-full h-14 text-base font-bold transition-colors ${pronto ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-300 hover:bg-slate-300 text-slate-600 cursor-pointer"}`}
        data-testid="btn-confirmar-recebimento">
        <CheckCircle2 className="h-5 w-5 mr-2" />
        {enviando ? "Enviando…" : pronto ? "Confirmar e assinar recebimento"
          : `Faltam ${pendencias.length} ${pendencias.length === 1 ? "item" : "itens"} para assinar`}
      </Button>
    </div>
  );
};
